import { Fragment, useCallback, useMemo } from "react";

import { getExtensionValue } from "@medplum/core";
import { Questionnaire, QuestionnaireItem, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { Box, Group, rem, SimpleGrid, Title, TitleOrder, UnstyledButton } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { IconCornerDownRight, IconPlus, IconTrash } from "@tabler/icons-react";
import omit from "lodash/omit";
import { v4 as uuidv4 } from "uuid";

import { TextWrapper } from "@/components/TextWrapper";
import { QuestionnaireContext } from "@/contexts/questionnaire";
import { Extensions } from "@/fhir/enums/extensions";
import { INTAKE_FORMS_FIELDS } from "@/intake-forms/constants/intake-forms";
import { EnableWhenBehavior } from "@/intake-forms/types/intake-forms";
import { isEnableWhenSatisfied } from "@/intake-forms/utils/enable-when";
import { questionnaireFormsetActionTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { updateNestedValue } from "@/utils/obj";
import {
  GenericFormValues,
  getFormsetCodeLinkId,
  getFormsetIdentifierLinkId,
} from "@/utils/questionnaires";

interface QuestionnaireFormItemsProps<T> {
  group: QuestionnaireItem;
  form: UseFormReturnType<T>;
  questionnaireResponse?: QuestionnaireResponseItem;
  parentPath?: string;
  isHorizontal?: boolean;
  label?: string;
  position?: number;
  groupTitleOrder?: TitleOrder;
  canShowControlledFormset?: boolean;
  flags?: string[];
}

interface QuestionnaireFormProps<T> extends Omit<QuestionnaireFormItemsProps<T>, "group"> {
  questionnaire?: Questionnaire | undefined;
  group: QuestionnaireItem | undefined;
  groupTitleOrder?: TitleOrder;
}

interface HorizontalGroupWrapperProps<T> extends QuestionnaireFormItemsProps<T> {
  isHorizontal: boolean;
  label?: string;
}

export default function QuestionnaireForm<T>({
  questionnaire,
  group,
  form,
  questionnaireResponse,
  flags,
  parentPath = "",
  label = "",
  isHorizontal = false,
  groupTitleOrder,
  canShowControlledFormset = false,
}: QuestionnaireFormProps<T>) {
  if (!group) return null;

  if (!questionnaire) {
    return (
      <QuestionnaireFormset
        form={form}
        group={group}
        label={label}
        flags={flags}
        parentPath={parentPath}
        isHorizontal={isHorizontal}
        groupTitleOrder={groupTitleOrder}
        questionnaireResponse={questionnaireResponse}
        canShowControlledFormset={canShowControlledFormset}
      />
    );
  }

  return (
    <QuestionnaireContext.Provider value={questionnaire}>
      <QuestionnaireFormset
        form={form}
        group={group}
        label={label}
        flags={flags}
        parentPath={parentPath}
        isHorizontal={isHorizontal}
        groupTitleOrder={groupTitleOrder}
        questionnaireResponse={questionnaireResponse}
        canShowControlledFormset={canShowControlledFormset}
      />
    </QuestionnaireContext.Provider>
  );
}

const QuestionnaireFormset = <T,>({
  group,
  form,
  questionnaireResponse,
  parentPath = "",
  label = "",
  isHorizontal = false,
  groupTitleOrder,
  canShowControlledFormset,
  flags,
}: QuestionnaireFormItemsProps<T>) => {
  const actionLabel = getExtensionValue(group, Extensions.QuestionnaireItemFormset)?.toString();
  const groupNameTitle =
    getExtensionValue(group, Extensions.QuestionnaireGroupNameTitle)?.toString() ?? "Occurrence";

  const informationText = getExtensionValue(
    group,
    Extensions.QuestionnaireInformationText,
  )?.toString();
  const shouldShowTitle = getExtensionValue(
    group,
    Extensions.QuestionnaireShouldShowTitle,
  )?.toString();
  const isNestedFormset =
    !!actionLabel &&
    (getExtensionValue(group, Extensions.QuestionnaireItemControlledFormset) as
      | boolean
      | undefined);

  const { setValues, values: formValues } = form;
  const linkId = group.linkId!;
  const items = group.item;
  const isFormset = group.repeats && actionLabel;
  const shouldBlockFirstItemDeletion = getExtensionValue(
    group,
    Extensions.QuestionnaireItemFormsetShouldBlockFirstItemDelete,
  ) as boolean | undefined;
  const getFormsetValues = useCallback(
    (values: Partial<T>): [GenericFormValues, string[]] => {
      const formValues = values as GenericFormValues;

      if (!parentPath) return [(formValues[linkId] ?? {}) as GenericFormValues, [linkId]];
      if (!parentPath.includes("."))
        return [(formValues[parentPath] ?? {}) as GenericFormValues, [parentPath]];

      const levels = parentPath.split(".");
      let formsetValues = formValues;
      for (const level of levels) {
        formsetValues = (formsetValues?.[level] ?? {}) as GenericFormValues;
      }

      return [formsetValues, levels];
    },
    [linkId, parentPath],
  );

  const handleAdd = () => {
    const codeLinkId = getFormsetCodeLinkId(items);
    const identifierLinkId = getFormsetIdentifierLinkId(items);
    if (!codeLinkId) return;

    setValues((prev) => {
      const [formsetValues, levels] = getFormsetValues(prev);

      const keys = Object.keys(formsetValues).map(Number);
      const newHighestValue = (keys.length ? Math.max(...keys) + 1 : 0).toString();

      let code;
      if (isNestedFormset) {
        code = parentPath.split(".").slice(-1)[0];
      } else {
        code = newHighestValue;
      }

      const identifierKey = identifierLinkId ? { [identifierLinkId]: uuidv4() } : {};

      // Create the new formset item with the code
      const newFormsetItem: Record<string, unknown> = {
        ...formsetValues,
        [newHighestValue]: {
          [codeLinkId]: code,
          ...identifierKey,
        },
      };

      // Initialize nested horizontal groups and auto-populate datetime fields
      if (items) {
        initializeHorizontalGroupFormValues(
          items,
          newFormsetItem[newHighestValue] as Record<string, unknown>,
        );
      }

      return updateNestedValue(prev, levels, newFormsetItem) as Partial<T>;
    });
  };

  const handleDelete = (key: string) => {
    const codeLinkId = getFormsetCodeLinkId(items);
    if (!codeLinkId) return;

    setValues((prev) => {
      const [formsetValues, levels] = getFormsetValues(prev);
      return updateNestedValue(prev, levels, omit(formsetValues, key)) as Partial<T>;
    });
  };

  const formsetKeys = useMemo(() => {
    const [formsetValues] = getFormsetValues(formValues);
    return Object.keys(formsetValues);
  }, [getFormsetValues, formValues]);

  const formsetError = form.errors[linkId];

  if (!isFormset) {
    return (
      <HorizontalGroupWrapper
        form={form}
        group={group}
        label={label}
        flags={flags}
        parentPath={parentPath}
        isHorizontal={isHorizontal}
        groupTitleOrder={groupTitleOrder}
        questionnaireResponse={questionnaireResponse}
        canShowControlledFormset={canShowControlledFormset}
      />
    );
  }

  return (
    <>
      {shouldShowTitle && <Title order={1}>{group.text}</Title>}
      {formsetError && (
        <TextWrapper c="red.9" variant="bold">
          {formsetError}
        </TextWrapper>
      )}
      {informationText && (
        <TextWrapper c="gray.7" variant="bold">
          {informationText}
        </TextWrapper>
      )}
      {formsetKeys.map((key, i) => (
        <Fragment key={key}>
          <Group mt="xs" justify="space-between">
            <TextWrapper
              size="md"
              c="gray.7"
              variant="bold"
              style={{ display: "flex", alignItems: "center" }}
            >
              <IconCornerDownRight size="1rem" color="#868E96" /> {groupNameTitle} #{i + 1}
            </TextWrapper>
            {(!shouldBlockFirstItemDeletion || (shouldBlockFirstItemDeletion && i !== 0)) && (
              <UnstyledButton tabIndex={-1} w="fit-content" onClick={() => handleDelete(key)}>
                <TextWrapper c="red.9" variant="bold">
                  <IconTrash />
                </TextWrapper>
              </UnstyledButton>
            )}
          </Group>
          <QuestionnaireItems
            form={form}
            position={i}
            group={group}
            flags={flags}
            data-testid={`formset-${key}`}
            groupTitleOrder={groupTitleOrder}
            parentPath={`${parentPath}.${key}`}
            questionnaireResponse={questionnaireResponse}
            canShowControlledFormset={canShowControlledFormset}
          />
        </Fragment>
      ))}
      <UnstyledButton
        p="xs"
        bg="gray.0"
        onClick={() => handleAdd()}
        data-testid={questionnaireFormsetActionTestId(linkId)}
      >
        <Group justify="center">
          <IconPlus style={{ stroke: "var(--mantine-color-gray-6)" }} />
          <TextWrapper variant="bold" c="burgundy.9" td="underline">
            {actionLabel}
          </TextWrapper>
        </Group>
      </UnstyledButton>
    </>
  );
};

const isHorizontalGroupItem = (item: QuestionnaireItem): boolean =>
  Boolean(
    item.type === "group" &&
      item.extension?.some(
        (ext) => ext.valueCodeableConcept?.coding?.[0]?.code === "horizontal-group",
      ),
  );

const initializeHorizontalGroupFormValues = (
  items: QuestionnaireItem[] | undefined,
  target: Record<string, unknown>,
) => {
  items?.forEach((item) => {
    if (isHorizontalGroupItem(item)) {
      const nestedTarget: Record<string, unknown> = {};
      target[item.linkId] = nestedTarget;
      initializeHorizontalGroupFormValues(item.item, nestedTarget);
      return;
    }

    if (item.type === "dateTime") {
      const hasAutoPopulate = getExtensionValue(item, Extensions.QuestionnaireFieldAutoPopulate);
      const formControlCodeableConcept = getExtensionValue(
        item,
        Extensions.QuestionnaireItemControl,
      );
      const formControlType = (formControlCodeableConcept as { coding?: { code?: string }[] })
        ?.coding?.[0]?.code;

      if (hasAutoPopulate && formControlType === "timestamp-display") {
        target[item.linkId] = new Date().toISOString();
      }
    }
  });
};

const isLayoutCandidateItem = (
  question: QuestionnaireItem,
  {
    flags,
    canShowControlledFormset,
  }: {
    flags?: string[];
    canShowControlledFormset?: boolean;
  },
): boolean => {
  if (
    !INTAKE_FORMS_FIELDS[question.type] ||
    getExtensionValue(question, Extensions.QuestionnaireItemHidden) ||
    (!canShowControlledFormset &&
      getExtensionValue(question, Extensions.QuestionnaireItemControlledFormset))
  ) {
    return false;
  }

  const flag = getExtensionValue(question, Extensions.QuestionFlag) as string | undefined;
  if (flag && !flags?.includes(flag)) {
    return false;
  }

  return true;
};

const QuestionnaireItems = <T,>({
  group,
  form,
  questionnaireResponse,
  parentPath = "",
  position = 0,
  groupTitleOrder,
  canShowControlledFormset,
  flags,
  isHorizontal = false,
}: QuestionnaireFormItemsProps<T>) => {
  const layoutItems = useMemo(
    () =>
      (group.item ?? []).filter((item) =>
        isLayoutCandidateItem(item, { flags, canShowControlledFormset }),
      ),
    [group.item, flags, canShowControlledFormset],
  );

  const visibleHorizontalItemCount = useMemo(() => {
    if (!isHorizontal) return 0;

    return layoutItems.filter((item) =>
      isEnableWhenSatisfied({
        enableWhen: item.enableWhen,
        formValues: form.values as Record<string, unknown>,
        parentPath,
        enableWhenBehavior: item.enableBehavior as EnableWhenBehavior | undefined,
      }),
    ).length;
  }, [isHorizontal, layoutItems, form.values, parentPath]);

  const fields = group.item?.map((question) => {
    if (!isLayoutCandidateItem(question, { flags, canShowControlledFormset })) {
      return null;
    }

    // Check if any choice field in this group has the inline enableWhen extension
    const hasChoiceWithInlineEnableWhen = group.item?.some(
      (item) =>
        item.type === "choice" &&
        (getExtensionValue(item, Extensions.QuestionnaireItemInlineEnableWhen) as boolean),
    );

    // Only skip items that have enableWhen conditions AND are part of a group with a choice field that has inline enableWhen
    // AND the enableWhen condition references that choice field
    if (hasChoiceWithInlineEnableWhen && question.enableWhen?.length) {
      const hasEnableWhenForChoiceInGroup = question.enableWhen.some((enableWhen) => {
        // Check if the enableWhen condition references a choice field in the same group that has inline-enable-when
        const referencedQuestion = group.item?.find((item) => item.linkId === enableWhen.question);
        return (
          referencedQuestion &&
          referencedQuestion.type === "choice" &&
          (getExtensionValue(
            referencedQuestion,
            Extensions.QuestionnaireItemInlineEnableWhen,
          ) as boolean)
        );
      });

      if (hasEnableWhenForChoiceInGroup) {
        // Skip rendering this item here - it will be rendered inline by the checkbox component
        return null;
      }
    }

    if (
      isHorizontal &&
      !isEnableWhenSatisfied({
        enableWhen: question.enableWhen,
        formValues: form.values as Record<string, unknown>,
        parentPath,
        enableWhenBehavior: question.enableBehavior as EnableWhenBehavior | undefined,
      })
    ) {
      return null;
    }

    const component = INTAKE_FORMS_FIELDS[question.type]!;
    const formFieldName = parentPath ? `${parentPath}.${question.linkId}` : question.linkId;

    const field = component({
      group,
      key: question.linkId,
      form,
      formFieldName,
      label: question.text || "",
      name: question.linkId,
      required: question.required,
      questionnaireItem: question,
      questionnaireResponseItem: questionnaireResponse?.item?.filter(
        (response) => response.linkId === question.linkId,
      )[position],
      enableWhen: question?.enableWhen,
      enableWhenBehavior: question?.enableBehavior as EnableWhenBehavior | undefined,
      rightSection:
        (getExtensionValue(question, Extensions.QuestionnaireFieldRightSection) as string) ?? "",
      placeholder: getExtensionValue(question, Extensions.QuestionnaireFieldPlaceholder) as string,
      icon: (getExtensionValue(question, Extensions.QuestionnaireFieldIcon) as string) ?? "",
      mask: (getExtensionValue(question, Extensions.QuestionnaireFieldMask) as string) ?? "",
      minRange: getExtensionValue(question, Extensions.QuestionnaireItemMinRange) as
        | number
        | undefined,
      maxRange: getExtensionValue(question, Extensions.QuestionnaireItemMaxRange) as
        | number
        | undefined,
      minValue: getExtensionValue(question, Extensions.QuestionnaireItemMinValue) as
        | number
        | undefined,
      maxValue: getExtensionValue(question, Extensions.QuestionnaireItemMaxValue) as
        | number
        | undefined,
      maxDecimalPlaces: getExtensionValue(
        question,
        Extensions.QuestionnaireItemMaxDecimalPlaces,
      ) as number | undefined,
      allowDecimal: getExtensionValue(question, Extensions.QuestionnaireItemAllowDecimal) as
        | boolean
        | undefined,
      autoPopulate: getExtensionValue(question, Extensions.QuestionnaireFieldAutoPopulate) as
        | boolean
        | undefined,
      itemOrder: getExtensionValue(question, Extensions.QuestionnaireItemOrder) as
        | number
        | undefined,
      hasCodingInputDetails: getExtensionValue(question, Extensions.HasCodingInputDetails) as
        | boolean
        | undefined,
      canSelectFutureDates: getExtensionValue(
        question,
        Extensions.QuestionnaireFieldDateCanSelectFutureDates,
      ) as boolean | undefined,
      valueSetIsCreatable: getExtensionValue(question, Extensions.ValueSetIsCreatable) as
        | boolean
        | undefined,
      groupTitleOrder,
    });

    if (!isHorizontal || visibleHorizontalItemCount === 0) {
      return field;
    }

    return (
      <Box miw={0} w="100%" key={question.linkId}>
        {field}
      </Box>
    );
  });

  if (!isHorizontal || visibleHorizontalItemCount === 0) {
    return fields;
  }

  // One field per row on phones and at most two on tablets, so fields never squeeze to a sliver.
  return (
    <SimpleGrid
      w="100%"
      spacing="md"
      cols={{
        base: 1,
        sm: Math.min(visibleHorizontalItemCount, 2),
        md: visibleHorizontalItemCount,
      }}
    >
      {fields}
    </SimpleGrid>
  );
};

const HorizontalGroupWrapper = <T,>({
  isHorizontal,
  label,
  ...itemsProps
}: HorizontalGroupWrapperProps<T>) => {
  if (!isHorizontal) {
    return <QuestionnaireItems {...itemsProps} />;
  }

  return (
    <>
      {label && <TextWrapper mb={rem(-16)}>{label}</TextWrapper>}
      <QuestionnaireItems {...itemsProps} isHorizontal />
    </>
  );
};
